import { useState } from 'react'
import { apiUrl, authedFetch } from '../utils/shopifyFetch'

/**
 * Fetch the CSV with credentials, then hand the browser a blob.
 *
 * It used to be an `<a href download>`, which cannot work here: a link is a
 * browser navigation, and a navigation carries no App Bridge session token. The
 * API refuses it — "Unauthorized — session token required" — and because the
 * browser is downloading rather than reading, it saves that JSON under a .csv
 * name. The seller opens a spreadsheet and gets a parse error, with nothing
 * anywhere saying the request was simply never authorised.
 *
 * Adding ?shop= does not rescue it; the middleware wants the token, not the
 * domain. So the file is fetched the same way every other call is made, and
 * only then turned into a download.
 */
async function downloadCsv(url, filename, onError) {
  try {
    const res = await authedFetch(url)
    if (!res.ok) {
      // Read the reason rather than saving it to disk as a spreadsheet.
      let why = `${res.status}`
      try { why = (await res.json())?.error || why } catch { /* not json */ }
      throw new Error(why)
    }
    const blob = await res.blob()
    const href = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = href
    a.download = filename
    document.body.appendChild(a)
    a.click()
    a.remove()
    // Freed on the next tick; revoking immediately cancels the download in
    // some browsers before it has read the blob.
    setTimeout(() => URL.revokeObjectURL(href), 10000)
  } catch (e) {
    onError?.(e.message || 'Could not download the file')
  }
}

/**
 * What an activity entry actually did.
 *
 * The log said "Synced to eBay: Test Hoodie (title, price)" and stopped there,
 * leaving both questions a seller has: which products, and what changed on each.
 * A nightly run said only how many.
 *
 * One product or order is shown in full. A run over many is shown as a table,
 * failures first — those are what the entry is opened for — with a CSV for the
 * cases that belong in a spreadsheet rather than a panel.
 */

const STATUS = {
  ok:      { label: 'done',    color: 'var(--green)' },
  failed:  { label: 'failed',  color: 'var(--red)'   },
  skipped: { label: 'skipped', color: 'var(--text3)' },
}

/** Failures first, then skipped: a seller opens this for what went wrong. */
const ORDER = { failed: 0, skipped: 1, ok: 2 }

const val = v => (v === null || v === undefined || v === '') ? '—' : String(v)

/** price: 19.99 → 24.50, for one item's changed fields. */
function Changes({ changes }) {
  const keys = Object.keys(changes || {})
  if (!keys.length) return null
  return (
    <div style={{ display:'flex', flexDirection:'column', gap:3 }}>
      {keys.map(k => (
        <div key={k} style={{ fontSize:12 }}>
          <span style={{ color:'var(--text3)' }}>{k}</span>{' '}
          <span style={{ fontFamily:'var(--mono)' }}>{val(changes[k].from)}</span>
          <span style={{ color:'var(--text3)' }}> → </span>
          <span style={{ fontFamily:'var(--mono)', color:'var(--green)' }}>{val(changes[k].to)}</span>
        </div>
      ))}
    </div>
  )
}

export default function ActivityDetails({ item, shop }) {
  const meta  = item?.metadata || {}
  const items = Array.isArray(meta.items) ? meta.items : []

  // Above the early return, because hooks must run in the same order on every
  // render. Declared after it, opening an entry with no items would render
  // fewer hooks than the last one and React would throw.
  const [csvBusy, setCsvBusy]   = useState(false)
  const [csvError, setCsvError] = useState(null)

  // Nothing structured to show: an entry from before this, or one that never
  // touched a product. Saying so beats an empty panel that looks broken.
  if (!items.length) {
    return (
      <div style={{ fontSize:12, color:'var(--text3)', padding:'10px 0 2px' }}>
        No per-product detail was recorded for this event.
        {meta.action ? ` (${meta.action})` : ''}
      </div>
    )
  }

  const rows    = [...items].sort((a, b) => (ORDER[a.status] ?? 3) - (ORDER[b.status] ?? 3))
  const single  = rows.length === 1
  const counts  = meta.counts || {}
  const csvHref = shop ? apiUrl(`/activity/${item.id}/details.csv`, shop) : null

  return (
    <div style={{ padding:'10px 0 2px' }}>
      <div style={{ display:'flex', gap:10, alignItems:'center', flexWrap:'wrap', marginBottom:10 }}>
        {meta.source && (
          <span className="badge badge-gray" style={{ fontSize:10 }}>{meta.source}</span>
        )}
        {!single && (
          <span style={{ fontSize:12, color:'var(--text2)' }}>
            {counts.ok || 0} done
            {counts.failed  ? <span style={{ color:'var(--red)' }}>{` · ${counts.failed} failed`}</span> : null}
            {counts.skipped ? <span style={{ color:'var(--text3)' }}>{` · ${counts.skipped} skipped`}</span> : null}
          </span>
        )}
        {/* A run over 300 products belongs in a spreadsheet, sorted by what
            failed — not in a panel. */}
        {!single && csvHref && (
          <button className="btn btn-sm" style={{ marginLeft:'auto' }} disabled={csvBusy}
            onClick={async () => {
              setCsvBusy(true); setCsvError(null)
              await downloadCsv(csvHref, `activity-${item.id}.csv`, setCsvError)
              setCsvBusy(false)
            }}>
            {csvBusy ? 'Preparing…' : '↓ Download details (CSV)'}
          </button>
        )}
        {csvError && (
          <span style={{ fontSize:11, color:'var(--red)' }}>
            Could not download: {csvError}
          </span>
        )}
      </div>

      {single ? (
        <div style={{ background:'var(--bg3)', border:'1px solid var(--border)',
                      borderRadius:'var(--radius)', padding:'10px 12px' }}>
          <div style={{ fontSize:13, fontWeight:600, marginBottom:2 }}>{rows[0].title}</div>
          <div style={{ fontSize:11, color:'var(--text3)', fontFamily:'var(--mono)', marginBottom:8 }}>
            {[rows[0].sku && `SKU ${rows[0].sku}`,
              rows[0].listingId && `listing ${rows[0].listingId}`,
              rows[0].orderId && `order ${rows[0].orderId}`].filter(Boolean).join(' · ') || ' '}
          </div>
          <Changes changes={rows[0].changes}/>
          {!!rows[0].fields?.length && (
            <div style={{ fontSize:12, color:'var(--text2)' }}>
              <span style={{ color:'var(--text3)' }}>Written: </span>{rows[0].fields.join(', ')}
            </div>
          )}
          {rows[0].error && (
            <div style={{ fontSize:12, color:'var(--red)', marginTop:6 }}>{rows[0].error}</div>
          )}
          {!!meta.images?.length && (
            <div style={{ display:'flex', gap:6, flexWrap:'wrap', marginTop:8 }}>
              {meta.images.map((src, i) => (
                <img key={i} src={src} alt={`Image ${i+1}`} loading="lazy"
                     style={{ width:40, height:40, objectFit:'cover', borderRadius:4,
                              border:'1px solid var(--border)' }}/>
              ))}
            </div>
          )}
        </div>
      ) : (
        <div className="table-wrap" style={{ maxHeight:320, overflowY:'auto' }}>
          <table>
            <thead><tr>
              <th>Product / order</th><th>SKU</th><th>What changed</th><th>Result</th>
            </tr></thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={i}>
                  <td style={{ fontSize:12 }}>
                    {r.title}
                    {(r.listingId || r.orderId) && (
                      <div style={{ fontSize:10, color:'var(--text3)', fontFamily:'var(--mono)' }}>
                        {r.listingId ? `listing ${r.listingId}` : `order ${r.orderId}`}
                      </div>
                    )}
                  </td>
                  <td style={{ fontFamily:'var(--mono)', fontSize:11 }}>{r.sku || '—'}</td>
                  <td>
                    <Changes changes={r.changes}/>
                    {!!r.fields?.length && (
                      <div style={{ fontSize:12, color:'var(--text2)' }}>{r.fields.join(', ')}</div>
                    )}
                    {r.error && <div style={{ fontSize:11, color:'var(--red)' }}>{r.error}</div>}
                    {!r.changes && !r.fields?.length && !r.error && (
                      <span style={{ fontSize:12, color:'var(--text3)' }}>nothing changed</span>
                    )}
                  </td>
                  <td>
                    <span style={{ fontSize:11, fontWeight:600,
                                   color:(STATUS[r.status] || STATUS.ok).color }}>
                      {(STATUS[r.status] || STATUS.ok).label}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* The cap is stated, so a capped entry is not mistaken for the whole run.
          The CSV holds the same rows, so it says this too. */}
      {meta.truncated > 0 && (
        <div style={{ fontSize:11, color:'var(--amber)', marginTop:8 }}>
          Showing the first {items.length} of {meta.total} — {meta.truncated} more were
          not recorded in detail.
        </div>
      )}
    </div>
  )
}
